import { Button, Colors, Text } from '@rockalingua/ui';

import {
  subscriptionTypes,
  yearDiscount,
} from '@rockalingua/shared/subscriptions/constants';
import usePaymentFlowAnalytics from '@/v2/domains/Analytics/hooks/usePaymentFlowAnalytics.hook';
import {
  PaymentFlowStep,
  PaymentSection,
} from '@/v2/domains/Analytics/types/payment';
import { useExpiredSubscriptionModalStore } from '@/v2/domains/Subscriptions/stores/useExpiredSubscriptionModal.store';
import useManageSubscriptionActions from '@/v2/domains/Subscriptions/hooks/useManageSubscriptionActions.hook';
import useGetSubscriptionTypesQuery from '@/v2/domains/Subscriptions/queries/useGetSubscriptionTypesQuery.hook';
import type { SubscriptionType } from '@/v2/domains/Subscriptions/types/subscription';
import ExpiredSubscriptionContainer from '../ExpiredSubscriptionBannerContainer/ExpiredSubscriptionContainer';

import {
  ButtonContainer,
  PrimaryButtonBlock,
  PriceContainer,
  SecondaryButtonBlock,
} from './FamilyExpiredSubscriptionBanner.styled';
import { useTrialOrPaidText } from '../../hooks/useTrialOrPaidText.hook';

type FamilyExpiredSubscriptionBannerProps = {
  title?: string;
  size?: 'large' | 'small';
};

export default function FamilyExpiredSubscriptionBanner({
  title,
  size = 'large',
}: FamilyExpiredSubscriptionBannerProps) {
  const { onOptionSelected, onSkip } = usePaymentFlowAnalytics({
    section: PaymentSection.paywall,
    step: PaymentFlowStep.payClicked,
  });
  const { subscribe } = useManageSubscriptionActions();
  const { setModalClosed } = useExpiredSubscriptionModalStore();
  const defaultTitle = useTrialOrPaidText();

  const { data } = useGetSubscriptionTypesQuery();
  const { family } = getBannerSubscriptionTypes(data as SubscriptionType[]);

  const handleContinue = () => {
    onOptionSelected({ option: family.code });
    setModalClosed(true);

    subscribe();
  };

  return (
    <ExpiredSubscriptionContainer
      title={title || defaultTitle}
      subtitle="You've explored Rockalingua's content. To keep using videos, games, and activities, choose how you'd like to continue."
      size={size}
    >
      <ButtonContainer>
        <PrimaryButtonBlock>
          <Button
            type="primary"
            size={size}
            color="accent"
            onClick={handleContinue}
          >
            Continue with {family.name}
          </Button>
          <PriceContainer>
            <Text variant="grey-16-medium" color="black">
              ${family.monthPrice}/month or ${family.yearPrice}
              /year (Save up to {yearDiscount})
            </Text>
            <Text variant="dm-14-regular" color={Colors.NEUTRAL_VARIANTS[90]}>
              Cancel anytime
            </Text>
          </PriceContainer>
        </PrimaryButtonBlock>
        {size == 'large' && (
          <SecondaryButtonBlock>
            <Button
              type="link"
              onClick={() => {
                onSkip();
                setModalClosed(true);
              }}
              size={size}
            >
              Leave for now
            </Button>
          </SecondaryButtonBlock>
        )}
      </ButtonContainer>
    </ExpiredSubscriptionContainer>
  );
}

function getBannerSubscriptionTypes(
  dynamicSubscriptionTypes: SubscriptionType[] = []
): {
  family: SubscriptionType;
} {
  const family =
    dynamicSubscriptionTypes.find(
      (subscriptionType): subscriptionType is SubscriptionType =>
        subscriptionType?.code == subscriptionTypes.family.code
    ) ?? subscriptionTypes.family;

  return {
    family,
  };
}
